<script setup>
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import BillingAPI from 'dashboard/api/billing';
import BillingCard from './BillingCard.vue';
import ButtonV4 from 'next/button/Button.vue';

const { t } = useI18n();
const isLoading = ref(true);
const invoices = ref([]);
const meta = ref({});
const error = ref(null);
const currentPage = ref(1);

const statusColors = {
  pending:
    'bg-amber-50 text-amber-700 dark:bg-amber-900/20 dark:text-amber-400',
  paid: 'bg-green-50 text-green-700 dark:bg-green-900/20 dark:text-green-400',
  overdue: 'bg-red-50 text-red-700 dark:bg-red-900/20 dark:text-red-400',
  cancelled:
    'bg-slate-50 text-slate-700 dark:bg-slate-900/20 dark:text-slate-400',
};

const statusLabels = {
  pending: 'BILLING.INVOICE.STATUS.PENDING',
  paid: 'BILLING.INVOICE.STATUS.PAID',
  overdue: 'BILLING.INVOICE.STATUS.OVERDUE',
  cancelled: 'BILLING.INVOICE.STATUS.CANCELLED',
};

const hasInvoices = computed(() => invoices.value.length > 0);
const hasPreviousPage = computed(() => currentPage.value > 1);
const hasNextPage = computed(() => currentPage.value < meta.value.total_pages);

const formatCurrency = value => {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(value);
};

const formatDate = dateString => {
  if (!dateString) return '-';
  const date = /^\d{4}-\d{2}-\d{2}$/.test(dateString)
    ? new Date(`${dateString}T00:00:00`)
    : new Date(dateString);
  return date.toLocaleDateString('pt-BR');
};

// Read the invoice snapshot: current add-ons may differ from the paid contract.
const invoiceItems = invoice => {
  const breakdown = invoice.breakdown || {};
  if (breakdown.mid_cycle) {
    return [
      {
        label: breakdown.mid_cycle.label,
        amount: invoice.total_amount,
      },
    ];
  }

  const items = [
    { label: t('BILLING.INVOICE.BASE_PLAN'), amount: invoice.base_plan_amount },
    ...(breakdown.account_addons || []).map(item => ({
      label: `${item.quantity} × ${item.name}`,
      amount: item.amount,
    })),
    ...(breakdown.additional_charges || []).map(item => ({
      label: item.description,
      amount: item.amount,
    })),
  ];
  // The legacy ai_agents_amount column also includes resources and modules.
  // Show only charges that reconcile to the recorded invoice total.
  const remainder =
    Number(invoice.total_amount) -
    items.reduce((sum, item) => sum + Number(item.amount), 0);
  if (Math.abs(remainder) >= 0.01) {
    return [
      { label: t('BILLING.INVOICE.TOTAL'), amount: invoice.total_amount },
    ];
  }
  return items.filter(item => Number(item.amount) !== 0);
};

const fetchInvoices = async (page = 1) => {
  isLoading.value = true;
  error.value = null;

  try {
    const response = await BillingAPI.getInvoiceHistory(page);
    invoices.value = response.data.invoices;
    meta.value = response.data.meta;
    currentPage.value = page;
  } catch (err) {
    error.value = err.message;
  } finally {
    isLoading.value = false;
  }
};

const goToPreviousPage = () => {
  if (hasPreviousPage.value) {
    fetchInvoices(currentPage.value - 1);
  }
};

const goToNextPage = () => {
  if (hasNextPage.value) {
    fetchInvoices(currentPage.value + 1);
  }
};

onMounted(() => fetchInvoices());
</script>

<template>
  <BillingCard
    :title="$t('BILLING.INVOICE.HISTORY_TITLE')"
    :description="$t('BILLING.INVOICE.HISTORY_DESCRIPTION')"
  >
    <div v-if="isLoading" class="flex items-center justify-center py-8">
      <div
        class="h-8 w-8 animate-spin rounded-full border-2 border-blue-500 border-t-transparent"
      />
    </div>

    <div v-else-if="error" class="px-5 py-4 text-red-600">
      {{ error }}
    </div>

    <div v-else-if="hasInvoices" class="space-y-4">
      <!-- Invoice List -->
      <div class="divide-y divide-n-weak">
        <div
          v-for="invoice in invoices"
          :key="invoice.id"
          class="flex items-center justify-between px-5 py-3"
        >
          <div class="flex-1">
            <div class="flex items-center gap-2">
              <span class="font-medium">
                {{
                  invoice.period_reference ||
                  formatDate(invoice.billing_period_start)
                }}
              </span>
              <span
                class="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium"
                :class="[statusColors[invoice.status]]"
              >
                {{ $t(statusLabels[invoice.status]) }}
              </span>
            </div>
            <div class="mt-1 text-sm text-n-slate-11">
              <span v-if="invoice.paid_at">
                {{ $t('BILLING.INVOICE.PAID_ON') }}:
                {{ formatDate(invoice.paid_at) }}
              </span>
              <span v-else-if="invoice.due_date">
                {{ $t('BILLING.INVOICE.DUE_DATE') }}:
                {{ formatDate(invoice.due_date) }}
              </span>
            </div>
            <div class="mt-1 text-xs text-n-slate-11">
              {{ $t('BILLING.INVOICE.PERIOD') }}:
              {{ formatDate(invoice.billing_period_start) }} –
              {{ formatDate(invoice.billing_period_end) }}
            </div>
            <!-- NFS-e: só aparece quando a nota fiscal já foi autorizada pelo Asaas -->
            <div v-if="invoice?.fiscal_invoice?.pdf_url" class="mt-1">
              <a
                :href="invoice.fiscal_invoice.pdf_url"
                target="_blank"
                rel="noopener noreferrer"
                class="text-xs font-medium text-n-brand hover:underline"
              >
                {{ $t('BILLING.INVOICE.FISCAL_PDF') }}
              </a>
            </div>
          </div>
          <div class="text-right">
            <div class="font-semibold">
              {{ formatCurrency(invoice.total_amount) }}
            </div>
            <div
              v-for="(item, index) in invoiceItems(invoice)"
              :key="index"
              class="mt-1 text-xs text-n-slate-11"
            >
              {{ item.label }}: {{ formatCurrency(item.amount) }}
            </div>
          </div>
        </div>
      </div>

      <!-- Pagination -->
      <div
        v-if="meta.total_pages > 1"
        class="flex items-center justify-between border-t border-n-weak px-5 py-3"
      >
        <span class="text-sm text-n-slate-11">
          {{ $t('BILLING.INVOICE.PAGE') }} {{ currentPage }}
          {{ $t('BILLING.INVOICE.OF') }}
          {{ meta.total_pages }}
        </span>
        <div class="flex gap-2">
          <ButtonV4
            sm
            faded
            slate
            :disabled="!hasPreviousPage"
            @click="goToPreviousPage"
          >
            {{ $t('BILLING.INVOICE.PREVIOUS') }}
          </ButtonV4>
          <ButtonV4
            sm
            faded
            slate
            :disabled="!hasNextPage"
            @click="goToNextPage"
          >
            {{ $t('BILLING.INVOICE.NEXT') }}
          </ButtonV4>
        </div>
      </div>
    </div>

    <div v-else class="px-5 py-8 text-center text-n-slate-11">
      {{ $t('BILLING.INVOICE.NO_HISTORY') }}
    </div>
  </BillingCard>
</template>
